// D:\docker-files\11.AI_trade3\3000.dashboard\src\pages\LoginPage.js 수정

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Brain, Eye, EyeOff, LogIn, Mail, RefreshCw, ArrowRight, AlertCircle } from 'lucide-react';
import { userService } from '../services/api';
import { setToken } from '../utils/auth';
import { getErrorMessage, handleAuthError } from '../utils/helpers';
import toast from 'react-hot-toast';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailVerificationNeeded, setEmailVerificationNeeded] = useState(null);
  const [isResendingCode, setIsResendingCode] = useState(false); // ✅ 추가

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 폼 입력시 에러 상태 초기화
    if (error) setError('');
    if (emailVerificationNeeded) setEmailVerificationNeeded(null);
  };

  const testApiConnection = async () => {
    try {
      const response = await fetch('/api/health');
      if (response.ok) {
        toast.success('API 서버 연결 성공!');
      } else {
        toast.error(`API 서버 응답 오류: ${response.status}`);
      }
    } catch (error) {
      toast.error(`API 연결 실패: ${error.message}`);
    }
  };

  // ✅ 인증번호 재발송 기능
  const handleResendVerificationCode = async () => {
    if (!emailVerificationNeeded?.email) return;
    
    setIsResendingCode(true);
    try {
      // 🔥 수정: 이메일 문자열만 전달하거나 다른 형식 시도
      console.log('📧 재발송 요청 이메일:', emailVerificationNeeded.email);
      
      // 옵션 1: 이메일 문자열만 전달
      await userService.resendVerificationCode(emailVerificationNeeded.email);
      
      toast.success('인증번호가 재발송되었습니다.');
    } catch (error) {
      console.error('Resend verification code error:', error);
      console.error('Error response:', error.response?.data);
      
      // 🔥 옵션 2: 실패시 다른 형식으로 재시도
      try {
        console.log('📧 다른 형식으로 재시도: { email: ... }');
        await userService.resendVerificationCode({ email: emailVerificationNeeded.email });
        toast.success('인증번호가 재발송되었습니다.');
      } catch (error2) {
        console.error('재시도도 실패:', error2);
        const errorMsg = getErrorMessage(error2);
        toast.error(`재발송 실패: ${errorMsg}`);
      }
    } finally {
      setIsResendingCode(false);
    }
  };

  // ✅ 다른 계정으로 로그인
  const handleTryAnotherAccount = () => {
    setEmailVerificationNeeded(null);
    setError('');
    setFormData(prev => ({ ...prev, email: '', password: '' }));
    toast.info('다른 계정으로 로그인해주세요.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailVerificationNeeded(null);
    setIsLoading(true);

    try {
      const response = await userService.login(formData);
      console.log('Login response:', response.data);
      
      toast.success('로그인 성공!');
      navigate('/account');
    } catch (error) {
      console.error('🚨 Login error:', error);
      console.log('🔍 에러 타입:', error.constructor.name);
      console.log('🔍 에러 응답:', error.response);
      console.log('🔍 에러 응답 데이터:', error.response?.data);
      
      console.log('🎯 handleAuthError 호출 시작');
      const authError = handleAuthError(error);
      console.log('🎯 handleAuthError 결과:', authError);
      
      if (authError.type === 'EMAIL_NOT_VERIFIED') {
        // ✅ 이메일 인증이 필요한 경우 - 로그인 페이지에 머물면서 안내
        setEmailVerificationNeeded({
          email: authError.email || formData.email,
          message: authError.message
        });
        setError('');
        // toast는 표시하지 않음 (UI에서 충분히 안내)
      } else {
        // 일반 에러인 경우
        setError(authError.message);
        toast.error('로그인 실패: ' + authError.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Brain className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            AI 트레이딩 시스템
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            계정에 로그인하여 시작하세요
          </p>
        </div>

        {/* API 테스트 버튼 */}
        <div className="text-center">
          <button
            onClick={testApiConnection}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            🔍 API 연결 테스트
          </button>
        </div>

        {/* 메인 카드 */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* ✅ 이메일 인증 필요 안내 (카드 상단에 배치) */}
          {emailVerificationNeeded && (
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-white">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">이메일 인증이 필요합니다</h3>
                  <p className="text-amber-100 mt-1 text-sm">
                    {emailVerificationNeeded.message}
                  </p>
                  <p className="text-amber-200 mt-1 text-xs">
                    계정: {emailVerificationNeeded.email}
                  </p>
                  
                  {/* 액션 버튼들 */}
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => navigate(`/verify-email?email=${encodeURIComponent(emailVerificationNeeded.email)}`)}
                      className="w-full bg-white text-orange-600 px-4 py-2.5 rounded-lg font-medium hover:bg-orange-50 transition-colors flex items-center justify-center"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      이메일 인증하러 가기
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={handleResendVerificationCode}
                        disabled={isResendingCode}
                        className="flex-1 bg-orange-400 hover:bg-orange-300 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
                      >
                        {isResendingCode ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        {isResendingCode ? '발송중...' : '재발송'}
                      </button>
                      
                      <button
                        onClick={handleTryAnotherAccount}
                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        다른 계정
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 로그인 폼 */}
          <div className="p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* 일반 에러 메시지 */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  이메일
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!!emailVerificationNeeded} // ✅ 인증 필요시 입력 비활성화
                  className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    emailVerificationNeeded 
                      ? 'border-gray-200 bg-gray-50 text-gray-500' 
                      : 'border-gray-300'
                  }`}
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    disabled={!!emailVerificationNeeded} // ✅ 인증 필요시 입력 비활성화
                    className={`w-full px-3 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      emailVerificationNeeded 
                        ? 'border-gray-200 bg-gray-50 text-gray-500' 
                        : 'border-gray-300'
                    }`}
                    placeholder="비밀번호 입력"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={!!emailVerificationNeeded}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* ✅ 이메일 인증 필요시 로그인 버튼 비활성화 */}
              <button
                type="submit"
                disabled={isLoading || !!emailVerificationNeeded}
                className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white font-medium transition-all duration-200 ${
                  emailVerificationNeeded
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    로그인 중...
                  </>
                ) : emailVerificationNeeded ? (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    이메일 인증을 먼저 완료해주세요
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    로그인
                  </>
                )}
              </button>
            </form>

            {/* 회원가입 링크 */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                계정이 없으신가요?{' '}
                <Link 
                  to="/register" 
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  회원가입
                </Link>
              </p>
            </div>

            {/* 디버그 정보 */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">
                🔧 디버그: API URL = {process.env.REACT_APP_API_URL || '/api'}
              </p>
              {emailVerificationNeeded && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ 이메일 인증 대기: {emailVerificationNeeded.email}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 추가 정보 */}
        <div className="text-center text-xs text-gray-500">
          <p>© 2024 AI Trading System. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;